<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<vl-layer-vector
  :visible="layer.visible"
  :extent="imageExtent"
  :update-while-interacting="false"
>

  <vl-source-vector
    ref="olSource"
    :loader-factory="loaderFactory"
    :strategy-factory="strategyFactory"
    url="-"
  > <!-- HACK because loader factory not used if URL not specified -->
    <vl-style-func :factory="styleFunctionFactory" />
  </vl-source-vector>

</vl-layer-vector>
</template>

<script>
import WKT from 'ol/format/WKT';
import {AnnotationCollection, Ims} from '@/api';
import {annotBelongsToLayer} from '@/utils/annotation-utils';
import {get} from '@/utils/store-helpers';

export default {
  name: 'annotation-layer',
  props: {
    index: String,
    layer: Object
  },
  data() {
    return {
      format: new WKT(),

      resolution: null,
      lastExtent: null,
      clustered: null,
      rasterPlane: false,
      planePending: false,          // plane decision in flight; do not fetch yet
      suppressedWhilePending: false, // a load was skipped while undecided
      pinnedIds: new Set(), // materialised on demand by a pick/selection; bounded by maxPins
      maxPins: 8,           // the handful a reader is actually working with

      maxResolutionNoClusters: null
    };
  },
  computed: {
    project: get('currentProject/project'),
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    annotsIdsToSelect() {
      return this.imageWrapper.selectedFeatures.annotsToSelect.map(annot => annot.id);
    },
    imageExtent() {
      return [0, 0, this.image.width, this.image.height];
    },
    selectedFeatures() {
      return this.imageWrapper.selectedFeatures.selectedFeatures;
    },
    ongoingEdit() {
      return this.imageWrapper.draw.ongoingEdit;
    },
    terms() {
      return this.imageWrapper.style.terms || [];
    },
    styleFunctionFactory() {
      // Force computed property update when one of those properties change (leading to new style function =>
      // rerendering - see https://github.com/ghettovoice/vuelayers/issues/68#issuecomment-404223423)
      this.imageWrapper.selectedFeatures.selectedFeatures;
      this.imageWrapper.style.layersOpacity;
      this.terms.forEach(term => {
        term.visible;
        term.color;
        term.opacity;
      });
      this.imageWrapper.style.displayNoTerm;
      this.imageWrapper.style.noTermOpacity;
      this.imageWrapper.properties.selectedPropertyKey;
      this.imageWrapper.properties.selectedPropertyColor;
      this.imageWrapper.review.reviewMode;
      this.imageWrapper.style.wrappedTracks.forEach(track => {
        track.color;
      });

      return () => {
        return this.$store.getters[this.imageModule + 'genStyleFunction'];
      };
    },
    reviewMode() {
      return this.imageWrapper.review.reviewMode;
    }
  },
  watch: {
    reviewMode() { // in review mode, reviewed annotation no longer displayed => need to force reload
      this.clearFeatures();
    }
  },
  methods: {
    // Drop every feature except the handful a pick/selection put there on purpose.
    pruneToPinned() {
      if (!this.$refs.olSource) {
        return;
      }
      const src = this.$refs.olSource.$source;
      const doomed = src.getFeatures().filter(f => !this.pinnedIds.has(f.getId()));
      if (!doomed.length) {
        return;
      }
      doomed.forEach(f => src.removeFeature(f));
    },

    clearFeatures(cache = true) {
      if (this.$refs.olSource) {
        this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', {layer: this.layer, cache});
        this.$refs.olSource.clearFeatures();
      }
    },

    // Pin an annotation so the raster-plane prune leaves it alone, keeping only the most
    // recent few.
    //
    // Pins used to accumulate for the whole session: measured 22 clicks -> 11 pins, never
    // shrinking. Each pin holds the annotation's FULL geometry, which on a UC multipolygon is
    // ~3.2 MB, so a long reading session would have put hundreds of megabytes back into the
    // browser - exactly what the raster plane exists to prevent.
    //
    // A Set preserves insertion order, so the oldest pin is simply the first entry. The
    // current selection is never evicted, however old it is.
    pinFeature(id) {
      this.pinnedIds.delete(id);          // re-pinning moves it to the newest position
      this.pinnedIds.add(id);
      if (this.pinnedIds.size <= this.maxPins) {
        return;
      }
      const selected = new Set(
        ((this.imageWrapper.selectedFeatures || {}).selectedFeatures || [])
          .map(f => (f.annot ? f.annot.id : f.id))
      );
      for (const old of [...this.pinnedIds]) {
        if (this.pinnedIds.size <= this.maxPins) {
          break;
        }
        if (selected.has(old)) {
          continue;                        // never drop what the reader is working on
        }
        this.pinnedIds.delete(old);
        const src = this.$refs.olSource && this.$refs.olSource.$source;
        const f = src && src.getFeatureById(old);
        if (f) {
          src.removeFeature(f);            // the pin was the only thing keeping it resident
        }
      }
    },

    // Clear the features AND OpenLayers' record of which extents are already loaded.
    //
    // `clearFeatures()` alone only empties the source. OpenLayers keeps `loadedExtentsRtree_`,
    // and it marks an extent as loaded even when our loader returns early without fetching -
    // which it does while a plane decision is in flight. So after the decision, clearing
    // features was not enough to bring the vectors back: the loader was never called again
    // and the layer sat at 1 feature where 498 were in view.
    forceReload() {
      if (!this.$refs.olSource) {
        return;
      }
      const src = this.$refs.olSource.$source;
      if (src && src.loadedExtentsRtree_) {
        src.loadedExtentsRtree_.clear();
      }
      this.clearFeatures();
    },

    annotBelongsToLayer(annot) {
      return annotBelongsToLayer(annot, this.layer, this.sliceIds);
    },

    addAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        this.$refs.olSource.addFeature(this.createFeature(annot));
      }
    },
    selectAnnotationHandler({annot, index}) {
      // The layer check needs `annot.user`, and ListAnnotationsBy does not request showUser -
      // so for a selection coming from the annotation list the check fails and nothing
      // happens. That is pre-existing, but on the raster plane it is fatal: this layer holds
      // no features, so there is no second chance later. The raster is already scoped to one
      // layer server-side (`users=`), so an annotation the raster drew belongs to this layer
      // by construction and the check adds nothing.
      const mine = this.annotBelongsToLayer(annot)
        || (this.rasterPlane && !Object.prototype.hasOwnProperty.call(annot, 'user'))
        // An enriched selection (geometry present, layer unknown) must highlight NOW on the
        // vector plane too: parking it until the destination viewport's bulk load finished
        // meant seconds of "the info card is here but nothing on the screen" after a
        // select-and-center jump (user-reported). The bulk load reconciles afterwards.
        || (!!annot.location && !Object.prototype.hasOwnProperty.call(annot, 'user'));
      if (index === this.index && mine && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature && (this.rasterPlane || annot.location)) {
          // On the raster plane this layer holds no features, so parking the annotation for a
          // later bulk load would wait forever: selecting from the annotation list, from
          // search, or from a raster click all silently did nothing. Materialise just this one
          // and select it. Every caller of selectAnnotationInLayer is fixed by doing it here
          // rather than at each call site.
          this.$refs.olSource.addFeature(this.createFeature(annot));
          olFeature = this.$refs.olSource.getFeatureById(annot.id);
        }
        // Pin whenever we are selecting on the raster plane, NOT only when this block was the
        // one that materialised the feature.
        //
        // A raster click resolves the annotation and adds it by another route, so `olFeature`
        // already existed here and the pin was skipped - then the next prune evicted the very
        // feature the reader had just selected. Measured: resident 1 at t+1.0 s with
        // `pinned: []`, resident 0 at t+1.5 s. Editing a clicked annotation could not work,
        // because the feature the modify tool needed was gone half a second later.
        if (this.rasterPlane) {
          this.pinFeature(annot.id);
        }
        if (!olFeature) {
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        } else {
          this.$store.dispatch(this.imageModule + 'selectFeature', olFeature);
        }
      }
    },
    reloadAnnotationsHandler({idImage, clear = false, hard = false} = {}) {
      if (!idImage || idImage === this.image.id) {
        if (clear) {
          this.clearFeatures();
        } else if (hard) {
          this.clearFeatures(false);
          this.loader();
        } else {
          this.loader();
        }
      }
    },
    reviewAnnotationHandler(annot) {
      if (this.reviewMode) { // if the image is in review mode, reviewed annotation should no longer be displayed on user layer => call delete handler
        this.deleteAnnotationHandler(annot);
      }
    },
    editAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature) {
          return;
        }
        olFeature.setGeometry(this.format.readGeometry(annot.location));
        olFeature.set('annot', annot);

        let indexSelectedFeature = this.selectedFeatures.findIndex(ftr => ftr.id === annot.id);
        if (indexSelectedFeature >= 0) {
          this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', {
            indexFeature: indexSelectedFeature,
            annot
          });
        }
      }
    },
    deleteAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature) {
          return;
        }
        this.$refs.olSource.removeFeature(olFeature);

        if (this.selectedFeatures.some(ftr => ftr.id === annot.id)) {
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
        }
      }
    },

    strategyFactory() {
      return (extent, resolution) => {
        this.lastExtent = extent;

        if (this.$refs.olSource && this.resolution && this.clustered !== null && ( // some features have already been loaded
          !this.clustered && resolution > this.maxResolutionNoClusters // recluster
                    || resolution !== this.resolution && this.clustered)) { // change of resolution while clustering

          // clear loaded extents to force reloading features
          this.$refs.olSource.$source.loadedExtentsRtree_.clear();
        }

        // Load the VIEWPORT, not the whole image. Loading the whole slide meant 24,433
        // features on the sputum slide even when 17 are in view - measured, at resolution 2.
        // Safe only because the `clustered` latch above is fixed: while it was stuck true the
        // reconcile took the getFeatures() branch and would have deleted every off-screen
        // feature. OpenLayers keeps loadedExtentsRtree_, so panning only fetches new area.
        return [extent];
      };
    },

    // ImsImage decides the plane on moveend and broadcasts it; this layer only obeys.
    onAnnotationPlane({plane}) {
      // 'pending' = the plane for this viewport is still being decided. Hold off fetching
      // rather than guessing: OpenLayers fires its loader on the same moveend that starts the
      // decision, so guessing "vector" costs a full load that is discarded milliseconds later
      // - measured ~2 fetches / ~200 KB crossing res 8 -> 16 on sputum.
      if (plane === 'pending') {
        this.planePending = true;
        this.suppressedWhilePending = false;
        clearTimeout(this._planePendingTimer);
        // Never leave the layer permanently silent because a decision went missing.
        this._planePendingTimer = setTimeout(() => {
          if (this.planePending) {
            this.planePending = false;
            this.forceReload();
          }
        }, 5000);
        return;
      }

      const wasSuppressed = this.suppressedWhilePending;
      this.planePending = false;
      this.suppressedWhilePending = false;
      clearTimeout(this._planePendingTimer);

      // 'unavailable' means the renderer is down AND this viewport is too dense to load as
      // vectors safely. Hold the layer in the same "do not fetch" state the raster plane uses,
      // minus the picture - ImsImage tells the reader why.
      const raster = plane === 'raster' || plane === 'unavailable';
      const changed = raster !== this.rasterPlane;
      this.rasterPlane = raster;

      if (raster) {
        // Prune NOW, do not wait for the loader.
        //
        // The prune lives inside the loader, but OpenLayers only calls the loader for an
        // extent it does not already consider loaded - so arriving on the raster plane over
        // ground the vector plane had already covered left everything sitting in the source.
        // Measured on sputum: 6,664 features still resident while the raster was displayed,
        // which is precisely the memory the raster plane exists to avoid.
        this.pruneToPinned();
      }

      if (!raster && (changed || wasSuppressed)) {
        // `wasSuppressed` matters: if the loader was skipped while the decision was in flight
        // and the answer turns out to be the plane we were already on, nothing else would ever
        // retrigger it and the vectors would simply never appear.
        if (changed) {
          this.pinnedIds.clear();
        }
        this.forceReload();
      }
    },

    async fetchAnnotations() {
      let annotations = (await Ims.instance.api.get(`/annotation-layers/${this.layer.id}/annotations`)).data;
      annotations.forEach((annotation) => {
        annotation.location = decodeURIComponent(atob(annotation.location));
        annotation.term = [];
        annotation.image = this.image.id;
        annotation.project = this.project.id;
      });

      return annotations;
    },
    async fetchAnnots(extent) {
      [0, 1].forEach(index => {
        if (extent[index] < 0) {
          extent[index] = 0;
        }
      });
      [2, 3].forEach(index => {
        if (this.imageExtent[index] < extent[index]) {
          extent[index] = this.imageExtent[index];
        }
      });

      let annots = await new AnnotationCollection({
        // `user`, NOT `username`. Core's annotation search filters on `user` (a user id) and
        // silently IGNORES `username` - so every layer was loading EVERY annotation on the
        // image, whatever layer it belonged to. Measured on image 142:
        //     username=55 -> 24,433 rows      user=55 -> 4 rows
        // That is the whole slide's annotations pulled into each layer, ~5.5 MB, on a view
        // whose visible layer holds four. It also broke the plane decision: the renderer was
        // asked "how many annotations are visible here?" (4, correctly scoped) and answered
        // "vectors are cheap", while the loader then fetched 24,433 - so the raster plane
        // never engaged on the very view that needed it most.
        user: !this.layer.isReview ? this.layer.id : null,
        image: this.image.id,
        slices: this.sliceIds,
        reviewed: this.layer.isReview,
        notReviewedOnly: !this.layer.isReview && this.reviewMode,
        bbox: extent.join(),
        // NOTE: display simplification intentionally disabled — we render the exact, full-fidelity
        // stored geometry. (Server still supports `geometrySimplification` if we re-enable it.)
        showWKT: true,
        showTerm: true,
        showGIS: true,
        showTrack: true,
        showLink: true,
        showImageGroup: true,
        kmeans: true
      }).fetchAll();

      return annots.array;
    },

    updateFeature(feature, annot) {
      let indexSelectedFeature = this.selectedFeatures.findIndex(ftr => ftr.id === feature.getId());
      let isFeatureSelected = indexSelectedFeature !== -1;

      if (!annot) {
        console.log(`Removing annot ${feature.getId()} in layer ${this.layer.id} (external action)`);
        this.$refs.olSource.removeFeature(feature);
        if (isFeatureSelected) {
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
        }
        return;
      }

      let storedAnnot = feature.get('annot');
      if (!this.clustered && annot.updated === storedAnnot.updated && this.sameTerms(annot.term, storedAnnot.term)) {
        // no modification performed since feature was loaded
        return;
      }

      if (isFeatureSelected) {
        if (this.ongoingEdit) {
          // if feature is selected and under modification, updating it may lead to conflict
          console.log(`Skipping update of selected annot ${annot.id} in layer ${this.layer.id} (ongoing edit)`);
          return;
        }
        console.log(`Updating selected annot ${annot.id} in layer ${this.layer.id} (external action)`);
        this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', {
          indexFeature: indexSelectedFeature,
          annot
        });
      }

      console.log(`Updating annot ${annot.id} in layer ${this.layer.id} (external action)`);
      feature.set('annot', annot);
      feature.setGeometry(this.format.readGeometry(annot.location));
    },

    async loader(extent = this.lastExtent, resolution = this.resolution) {
      this.resolution = resolution;
      this.lastExtent = extent;

      // Invisible layers don't load.
      if (!this.layer.visible || !extent) {
        return;
      }

      // Dense layers (large imported polygons) are expensive to fetch/simplify, and OpenLayers
      // fires the loader repeatedly on pan/zoom — overlapping fetches pile up on the server and
      // time out ("error fetching annotations"). Serialize: one fetch at a time, coalescing rapid
      // view changes into a single trailing re-run with the latest extent.
      if (this._loaderBusy) {
        this._loaderPending = true;
        return;
      }
      this._loaderBusy = true;
      try {
        do {
          this._loaderPending = false;
          await this._doLoad(this.lastExtent, this.resolution);
        } while (this._loaderPending);
      } finally {
        this._loaderBusy = false;
      }
    },

    // Ask the renderer whether this viewport is small enough to draw as features. It answers
    // 204 when it is, and the layer then behaves exactly as it always has. When it is not,
    // fetching anyway is not merely slow: image 90752 holds 60 annotations whose geometry is
    // 95,539,176 bytes, and requesting them OOM-kills core (exit 137). So this is a guard, not
    // an optimisation - it is what stops one slide taking the API down for everyone.

    async _doLoad(extent, resolution) {
      // The plane is decided once per view change in ImsImage.decideAnnotationPlane (on
      // moveend) and arrives here as the annotationPlane event; this layer only obeys it.
      // Deciding it here was unreliable: once the raster is in force this loader stops
      // fetching, OpenLayers still records the extent as loaded, and the loader is never
      // called again - so zooming back in left the raster stuck on and the vectors never
      // returned.
      if (this.planePending) {
        // Undecided: skip this load and remember that we did, so the decision can retrigger it.
        this.suppressedWhilePending = true;
        return;
      }
      if (this.rasterPlane) {
        // Prune on EVERY load, not just the transition. Clearing only on the transition left
        // whatever had already been loaded - 24,433 features on the sputum slide, loaded
        // before the first plane decision - sitting in the source for the whole session,
        // which is exactly the flood the raster exists to avoid.
        //
        // Everything goes except ids deliberately materialised by a pick or a list selection:
        // those are the annotation the reader is working on, and wiping them made the
        // selection vanish on the next moveend.
        this.pruneToPinned();
        return;
      }
      let arrayAnnots;
      this.$eventBus.$emit('annotationLoadingStart'); // drives the "Annotations loading" indicator
      try {
        if (Object.prototype.hasOwnProperty.call(this.layer, 'username')) {
          arrayAnnots = await this.fetchAnnots(extent);
          // Order by size, so bigger ones are always sent to back
          arrayAnnots.sort(
            function (a, b) {
              if (a.area < b.area) {
                return 1;
              } else {
                return -1;
              }
            }
          );
        } else {
          arrayAnnots = await this.fetchAnnotations();
        }
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-fetch-annotations-viewer')});
        return;
      } finally {
        this.$eventBus.$emit('annotationLoadingEnd');
      }

      if (!this.$refs.olSource) {
        return;
      }

      let wasClustered = this.clustered;
      if (arrayAnnots.length) {
        // Loose comparison on purpose: `count` is absent from Annotation._initProperties so it
        // arrives as `undefined`, and `undefined !== null` is true - which latched `clustered`
        // on permanently and made the guard in strategyFactory clear loadedExtentsRtree_ on
        // every resolution change, re-fetching the whole layer on EVERY zoom step.
        this.clustered = arrayAnnots[0].count != null;
        if (!this.clustered && resolution > this.maxResolutionNoClusters) {
          this.maxResolutionNoClusters = resolution;
        }
      }

      let annots = arrayAnnots.reduce((obj, annot) => {
        obj[annot.id] = annot;
        return obj;
      }, {});
      let seenAnnots = [];

      if (wasClustered !== null && wasClustered !== this.clustered) {
        this.clearFeatures(); // clearing features will retrigger the loader
      } else {
        let features = this.clustered ? this.$refs.olSource.$source.getFeatures()
          : this.$refs.olSource.$source.getFeaturesInExtent(extent);

        features.forEach(feature => {
          this.updateFeature(feature, annots[feature.getId()]);
          seenAnnots.push(feature.getId());
        });
      }

      arrayAnnots.forEach(annot => {
        if (!seenAnnots.includes(annot.id)) {
          this.$refs.olSource.addFeature(this.createFeature(annot));
        }
      });
    },

    loaderFactory() {
      return (extent, resolution) => this.loader(extent, resolution);
    },

    createFeature(annot) {
      let feature = this.format.readFeature(annot.location);
      feature.setId(annot.id);
      feature.set('annot', annot);

      if (this.annotsIdsToSelect.includes(annot.id)) {
        this.$store.dispatch(this.imageModule + 'selectFeature', feature);
      }

      return feature;
    },

    sameTerms(terms1, terms2) {
      if (terms1.length !== terms2.length) {
        return false;
      }
      return terms1.every(term => terms2.includes(term));
    }
  },
  mounted() {
    this.$eventBus.$on('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$on('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$on('selectAnnotationInLayer', this.selectAnnotationHandler);
    this.$eventBus.$on('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$on('reviewAnnotation', this.reviewAnnotationHandler);
    this.$eventBus.$on('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$on('deleteAnnotation', this.deleteAnnotationHandler);
  },
  beforeDestroy() {
    // unsubscribe from all events
    this.$eventBus.$off('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$off('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$off('selectAnnotationInLayer', this.selectAnnotationHandler);
    this.$eventBus.$off('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$off('reviewAnnotation', this.reviewAnnotationHandler);
    this.$eventBus.$off('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$off('deleteAnnotation', this.deleteAnnotationHandler);
  }
};
</script>
